"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, SlidersHorizontal as Sliders, ShieldAlert, Cpu } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Controller() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="compare-the-controller" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 border-b border-border">
      <div className="mx-auto max-w-5xl">
        {/* Machined spec panel with precision tick marks */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        border: "box",
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.3,
        ease: "easeOut"
      }} className="relative border border-border bg-card text-card-foreground p-6 sm:p-10 md:p-12 shadow-xs">
          {/* Corner Datum Tick Marks */}
          <div aria-hidden="true" className="absolute -top-1.5 -left-1.5 size-3 border-t-2 border-l-2 border-primary pointer-events-none" />
          <div aria-hidden="true" className="absolute -top-1.5 -right-1.5 size-3 border-t-2 border-r-2 border-primary pointer-events-none" />
          <div aria-hidden="true" className="absolute -bottom-1.5 -left-1.5 size-3 border-b-2 border-l-2 border-primary pointer-events-none" />
          <div aria-hidden="true" className="absolute -bottom-1.5 -right-1.5 size-3 border-b-2 border-r-2 border-primary pointer-events-none" />

          {/* Section Heading */}
          <div className="max-w-3xl mb-8">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight uppercase text-foreground leading-none">
              Compare the Controller, Not Just the Bull
            </h2>
          </div>

          {/* Structured Paragraph Content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-10 pt-2">
            <div className="border-t border-border/80 pt-4 flex flex-col justify-between">
              <div className="flex items-center text-xs font-mono text-primary mb-3">
                <Cpu className="size-4 shrink-0" />
              </div>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground font-default">
                The visible bull gets most of the attention, but the controller has a major role in how the machine operates. Different systems may provide different combinations of manual control, programmed modes, speed adjustment and motion control.
              </p>
            </div>

            <div className="border-t border-border/80 pt-4 flex flex-col justify-between">
              <div className="flex items-center text-xs font-mono text-primary mb-3">
                <Sliders className="size-4 shrink-0" />
              </div>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground font-default">
                Before choosing a mechanical bull machine for sale, review the controller supplied with that particular machine. Look at how the operator adjusts the ride, what movements it controls and which emergency-stop features are documented.
              </p>
            </div>

            <div className="border-t border-border/80 pt-4 flex flex-col justify-between">
              <div className="flex items-center text-xs font-mono text-primary mb-3">
                <ShieldAlert className="size-4 shrink-0" />
              </div>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground font-default">
                For a commercial buyer, those details can be more important over time than cosmetic differences such as bull color or arena graphics.
              </p>
            </div>
          </div>

          {/* Action Footer Strip */}
          <div className="border-t border-border pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-end gap-4">
            <Button asChild className="bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground transition-[background-color,color] duration-180 rounded-none h-11 px-6 font-mono text-xs tracking-wider uppercase shrink-0 shadow-none border border-secondary">
              <Link to="/MechanicalBullBuyingGuidePage" className="inline-flex items-center gap-2">
                <span>READ THE MECHANICAL BULL BUYING GUIDE</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}